import { useCallback, useState } from 'react';
import { EntityKey } from '@common/api';
import { Button } from '@components/button';
import { FormField } from '@components/form';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { Modal } from '@components/modal/Modal';
import { ModalFooter } from '@components/modal/ModalFooter';
import { useModal } from '@components/modal/ModalProvider';
import { ENTITY_FILTERS } from '@data/entity/search';
import { EditTags } from '@features/tags/EditTags';
import { ExpEditor } from '@features/expression/ExpEditor';
import { useLoading } from '@hooks/useLoading';
import { useI18nContext } from '@i18n/i18n-react';
import { incrementVersion } from '@util/number';

interface CopyFromModalProps {
    onDone: (formula: string) => Promise<void>;
}

interface CopyFromState {
    variable: EntityKey | null;
    members: EntityKey[];
    formula: string;
}

// The sum() formula a picker selection generates. Exported only to pin its output
// format (the string the backend evaluates) in a test.
export const buildFormula = (variable: EntityKey | null, members: EntityKey[]): string => {
    const refs: string[] = [];
    if (variable) refs.push(`$${variable.id}`);
    for (const m of members) refs.push(`$${m.id}`);
    return refs.length ? `sum(${refs.join(', ')})` : '';
};

export function CopyFromModal({ onDone }: CopyFromModalProps) {
    const { LL } = useI18nContext();
    const [loading, loadingWrapper] = useLoading();
    const { hideModal } = useModal();

    const [state, setState] = useState<CopyFromState>({ variable: null, members: [], formula: '' });
    const { variable, members, formula } = state;
    const [formulaError, setFormulaError] = useState<string | null>(null);
    const [editorVersion, setEditorVersion] = useState(0);

    // The formula mirrors the pickers until the user hand-edits it. Once it diverges
    // from what the selection generates, the pickers can no longer change it, so we
    // disable them and offer "Regenerate" to rebuild the formula from the selection.
    const generated = buildFormula(variable, members);
    const isGenerated = formula === generated;

    const handleVariableChange = useCallback(
        (tags: EntityKey[]) =>
            setState((s) => {
                const variable = tags[0] ?? null;
                return { ...s, variable, formula: buildFormula(variable, s.members) };
            }),
        []
    );
    const handleMembersChange = useCallback(
        (members: EntityKey[]) => setState((s) => ({ ...s, members, formula: buildFormula(s.variable, members) })),
        []
    );
    const handleFormulaChange = useCallback((formula: string) => setState((s) => ({ ...s, formula })), []);
    const handleRegenerate = useCallback(() => {
        setState((s) => ({ ...s, formula: buildFormula(s.variable, s.members) }));
        // Remount the editor (the prop alone may be unchanged) and clear the error it won't re-emit
        setFormulaError(null);
        setEditorVersion(incrementVersion);
    }, []);

    return (
        <Modal.Content width={500}>
            <Modal.Header title={LL.COPY_DATA_FROM()} />
            <Modal.Body>
                <FlexCol $gap={12}>
                    <FormField label={LL.VARIABLE()}>
                        <EditTags
                            kind={LL.VARIABLE()}
                            tags={variable ? [variable] : []}
                            limit={1}
                            entityFilter={ENTITY_FILTERS.variable}
                            onChange={handleVariableChange}
                            disabled={!isGenerated}
                        />
                    </FormField>
                    <FormField label={LL.ENTITY_TYPES.DIMENSION_MEMBER()}>
                        <EditTags
                            kind={LL.ENTITY_TYPE.DIMENSION_MEMBER()}
                            tags={members}
                            entityFilter={ENTITY_FILTERS.member}
                            onChange={handleMembersChange}
                            disabled={!isGenerated}
                        />
                    </FormField>
                    <FormField
                        label={
                            <FlexRow $justify="space-between" $align="center">
                                {LL.FORMULA()}
                                {!isGenerated && (
                                    <Button $type="quaternary" $size="small" onClick={handleRegenerate}>
                                        {LL.REGENERATE()}
                                    </Button>
                                )}
                            </FlexRow>
                        }
                    >
                        <ExpEditor
                            key={editorVersion}
                            // Picker output only — echoing the typed value back rewrites the doc mid-typing
                            expression={generated}
                            entityFilter={ENTITY_FILTERS.formula}
                            readOnly={false}
                            onChange={handleFormulaChange}
                            onErrorChange={setFormulaError}
                            uncontrolled
                        />
                    </FormField>
                </FlexCol>
            </Modal.Body>
            <ModalFooter
                done={loadingWrapper(async () => {
                    await onDone(formula);
                    hideModal();
                })}
                loading={loading}
                disabled={formula.trim().length === 0 || formulaError != null}
            />
        </Modal.Content>
    );
}
